function getCartDrawerForegroundTri(hostEl) {
  const raw = getComputedStyle(hostEl).getPropertyValue('--color-foreground').trim();
  return raw || '0, 0, 0';
}

function getCartDrawerDimRgba(hostEl, opacity = 0.5) {
  return `rgba(${getCartDrawerForegroundTri(hostEl)}, ${opacity})`;
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    if (typeof gsap !== 'undefined') {
      this.classList.add('drawer--motion-gsap');
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  trapFocusAfterOpen() {
    const containerToTrapFocusOn = this.classList.contains('is-empty')
      ? this.querySelector('.drawer__inner-empty')
      : document.getElementById('CartDrawer');
    const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
    trapFocus(containerToTrapFocusOn, focusElement);
  }

  openWithGsap() {
    const inner = this.querySelector('.drawer__inner');
    if (!inner) return;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const dimDuration = reduce ? 0.05 : 0.36;
    const slideDuration = reduce ? 0.05 : 0.76;

    if (this._cartDrawerTl) this._cartDrawerTl.kill();

    this.classList.add('animate', 'active');

    const dimTarget = getCartDrawerDimRgba(this);

    gsap.set(this, { backgroundColor: 'rgba(0, 0, 0, 0)' });
    gsap.set(inner, { xPercent: 100, force3D: true });

    this._cartDrawerTl = gsap.timeline({
      onComplete: () => {
        this._cartDrawerTl = null;
        this.trapFocusAfterOpen();
      },
    });

    this._cartDrawerTl
      .to(this, { backgroundColor: dimTarget, duration: dimDuration, ease: 'power2.out' })
      .to(inner, { xPercent: 0, duration: slideDuration, ease: 'power3.out' });
  }

  closeWithGsap() {
    const inner = this.querySelector('.drawer__inner');
    if (!inner) return;

    removeTrapFocus(this.activeElement);

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const dimDuration = reduce ? 0.05 : 0.34;
    const slideDuration = reduce ? 0.05 : 0.52;

    if (this._cartDrawerTl) this._cartDrawerTl.kill();

    this._cartDrawerTl = gsap.timeline({
      onComplete: () => {
        this._cartDrawerTl = null;
        this.classList.remove('active', 'animate');
        gsap.set(this, { clearProps: 'backgroundColor' });
        gsap.set(inner, { clearProps: 'transform' });
        document.body.classList.remove('overflow-hidden');
      },
    });

    this._cartDrawerTl
      .to(inner, { xPercent: 100, duration: slideDuration, ease: 'power3.in' })
      .to(this, { backgroundColor: 'rgba(0, 0, 0, 0)', duration: dimDuration, ease: 'power2.in' }, reduce ? 0 : '-=0.14');
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    document.body.classList.add('overflow-hidden');

    if (typeof gsap !== 'undefined' && this.classList.contains('drawer--motion-gsap')) {
      this.openWithGsap();
      return;
    }

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        this.trapFocusAfterOpen();
      },
      { once: true }
    );
  }

  close() {
    if (typeof gsap !== 'undefined' && this.classList.contains('drawer--motion-gsap')) {
      this.closeWithGsap();
      return;
    }

    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.cartItemVariantId = String(parsedState.variant_id || parsedState.id || '');
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    this.syncRecentlyAddedVariant(parsedState);

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  syncRecentlyAddedVariant(parsedState) {
    if (!this.cartItemVariantId) return;

    const drawerItems = this.querySelector('#CartDrawer-CartItems');
    if (!drawerItems) return;

    const quantityInput = drawerItems.querySelector(
      `.quantity__input[data-quantity-variant-id="${this.cartItemVariantId}"]`
    );
    const row = quantityInput?.closest('tr.cart-item');
    if (!row) return;

    const tbody = row.closest('tbody');
    if (tbody && tbody.firstElementChild !== row) {
      tbody.prepend(row);
    }

    const imageUrl = this.extractVariantImageUrl(parsedState);
    const imageEl = row.querySelector('.cart-item__image');
    if (imageEl && imageUrl) {
      imageEl.src = imageUrl;
    }

    const colorValue = this.extractColorValue(parsedState);
    if (colorValue) {
      const detailsCell = row.querySelector('.cart-item__details');
      if (!detailsCell) return;

      let colorOption = detailsCell.querySelector('.product-option[data-option-color]');
      if (!colorOption) {
        const dl = detailsCell.querySelector('dl');
        if (!dl) return;
        colorOption = document.createElement('div');
        colorOption.className = 'product-option';
        colorOption.setAttribute('data-option-color', 'true');
        colorOption.innerHTML = '<dt>Color:</dt><dd></dd>';
        dl.prepend(colorOption);
      }

      const dd = colorOption.querySelector('dd');
      if (dd) dd.textContent = colorValue;
    }
  }

  extractVariantImageUrl(parsedState) {
    const featuredImageUrl = parsedState?.featured_image?.url;
    if (featuredImageUrl) return featuredImageUrl;

    const image = parsedState?.image;
    if (typeof image === 'string' && image.trim()) return image;
    if (image?.src) return image.src;

    return '';
  }

  extractColorValue(parsedState) {
    const fromOptionArray = parsedState?.options_with_values?.find?.((option) => {
      const name = String(option?.name || '').toLowerCase();
      return name.includes('color') || name.includes('colour');
    });
    if (fromOptionArray?.value) return String(fromOptionArray.value);

    if (Array.isArray(parsedState?.options) && parsedState.options.length > 0) {
      return String(parsedState.options[0]);
    }

    const variantTitle = String(parsedState?.variant_title || '').trim();
    if (variantTitle && variantTitle.toLowerCase() !== 'default title') {
      const firstPart = variantTitle.split(' / ')[0]?.trim();
      return firstPart || variantTitle;
    }

    return '';
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
